import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import type { SalesSettings as SalesSettingsData } from '../../interfaces/sales/salesSettings.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';

const EMPTY_FORM: SalesSettingsData = {
  institutionId: '',
  arAccountId: '',
  defaultRevenueAccountId: '',
  taxPayableAccountId: '',
  defaultTaxRatePct: 0,
  defaultQuotationValidityDays: 30,
  defaultPaymentTermsDays: 30,
};

const SalesSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<SalesSettingsData>(EMPTY_FORM);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setLoading(true);
      const [settings, accountsRes] = await Promise.all([
        SalesSettingsService.getByInstitution(institutionId).catch(() => null),
        AccountService.getAllAccounts(),
      ]);
      setAccounts(accountsRes ?? []);
      if (settings) {
        setForm({
          institutionId: settings.institutionId ?? institutionId,
          arAccountId: settings.arAccountId ?? '',
          defaultRevenueAccountId: settings.defaultRevenueAccountId ?? '',
          taxPayableAccountId: settings.taxPayableAccountId ?? '',
          defaultTaxRatePct: settings.defaultTaxRatePct ?? 0,
          defaultQuotationValidityDays: settings.defaultQuotationValidityDays ?? 30,
          defaultPaymentTermsDays: settings.defaultPaymentTermsDays ?? 30,
        });
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load sales settings');
    } finally {
      setLoading(false);
    }
  };

  const setAccount = (k: keyof SalesSettingsData) =>
    (id: string) => setForm({ ...form, [k]: id });

  const handleNumberField = (k: keyof SalesSettingsData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const n = parseFloat(e.target.value);
      setForm({ ...form, [k]: Number.isFinite(n) ? n : 0 });
    };

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSaving(true);
      await SalesSettingsService.upsert({ ...form, institutionId });
      toast.showSuccess('Sales settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save sales settings');
    } finally {
      setSaving(false);
    }
  };

  const assetAccounts = accounts.filter(a => a.accountType === 'ASSET');
  const revenueAccounts = accounts.filter(a => a.accountType === 'REVENUE');

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Sales Settings</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        <AccountAutocomplete
          label="AR Account"
          accounts={assetAccounts}
          value={form.arAccountId ?? ''}
          onChange={setAccount('arAccountId')}
          placeholder="None"
        />

        <AccountAutocomplete
          label="Default Revenue Account"
          accounts={revenueAccounts}
          value={form.defaultRevenueAccountId ?? ''}
          onChange={setAccount('defaultRevenueAccountId')}
          placeholder="None"
        />

        <AccountAutocomplete
          label="Tax Payable Account"
          accounts={accounts}
          value={form.taxPayableAccountId ?? ''}
          onChange={setAccount('taxPayableAccountId')}
          placeholder="None"
        />

        <TextField
          label="Default Tax Rate (%)"
          type="number"
          value={form.defaultTaxRatePct ?? 0}
          onChange={handleNumberField('defaultTaxRatePct')}
          inputProps={{ min: 0, max: 100, step: 0.01, 'aria-label': 'Default Tax Rate' }}
          fullWidth
        />

        <TextField
          label="Default Quotation Validity (days)"
          type="number"
          value={form.defaultQuotationValidityDays ?? 30}
          onChange={handleNumberField('defaultQuotationValidityDays')}
          inputProps={{ min: 0, step: 1, 'aria-label': 'Default Quotation Validity Days' }}
          fullWidth
        />

        <TextField
          label="Default Payment Terms (days)"
          type="number"
          value={form.defaultPaymentTermsDays ?? 30}
          onChange={handleNumberField('defaultPaymentTermsDays')}
          inputProps={{ min: 0, step: 1, 'aria-label': 'Default Payment Terms Days' }}
          fullWidth
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default SalesSettings;
